// lib/hooks/usePermissions.ts
"use client";

import { permissionKey } from "@/lib/permission-checks";
import { usePrefetchedPermissions } from "@/contexts/prefetched-permissions-context";
import { useSessionContext } from "@/contexts/session-context";
import { useQuery } from "@tanstack/react-query";
import { authClient } from "@cartz/auth/client";

export type Perms = Record<string, string[]>;

export function usePermissions(permissions: Perms) {
  const { activeOrganizationId } = useSessionContext();
  const prefetchedPermissions = usePrefetchedPermissions();
  const key = permissionKey(permissions);
  const prefetchedValue = prefetchedPermissions?.[key];

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["permissions", activeOrganizationId, permissions],
    queryFn: async () => {
      const { data } = await authClient.organization.hasPermission({
        permissions: permissions,
      });

      return Boolean(data?.success);
    },
    staleTime: 20 * 60 * 1000,
    // Prefetch is a hint only — always verify with a client fetch so a failed
    // or stale server prefetch cannot stick as a 20-minute denial.
    placeholderData: prefetchedValue,
  });

  return {
    allowed: data ?? null,
    loading: isLoading,
    refetch,
  };
}

export function useBulkPermissions(permissions: Perms[]) {
  const uniquePermissions = Array.from(
    new Map(permissions.map((p) => [permissionKey(p), p])).values(),
  );

  const queries = uniquePermissions.map((permission) =>
    usePermissions(permission),
  );

  const allowed = new Map(
    uniquePermissions.map((permission, index) => [
      permissionKey(permission),
      queries[index].allowed ?? false,
    ]),
  );

  const loading = queries.some((query) => query.loading);
  const refetch = async () => {
    await Promise.all(queries.map((query) => query.refetch()));
  };

  return {
    allowed,
    loading,
    refetch,
  };
}
